/**
 * tour_hotels meta dizisinden otel ID'lerini Excel/import sırasıyla çıkarır (tekilleştirilmiş).
 * Alternatif otel ID'leri dahil edilir (isim çözümlemesi için); tablo satırları için değil.
 */
export function extractOrderedHotelIds(tourHotelsMeta: unknown): number[] {
    const ids: number[] = [];
    const seen = new Set<number>();

    extractOrderedHotelEntries(tourHotelsMeta).forEach((entry) => {
        if (!seen.has(entry.hotelId)) {
            seen.add(entry.hotelId);
            ids.push(entry.hotelId);
        }
        (entry.alternativeHotelIds ?? []).forEach((altId) => {
            if (!seen.has(altId)) {
                seen.add(altId);
                ids.push(altId);
            }
        });
    });

    return ids;
}

export interface TourHotelEntry {
    hotelId: number;
    package?: string;
    nights?: number;
    meals?: string[];
    mealsCustom?: string;
    alternativeHotelIds?: number[];
    /** React key — aynı otel farklı paketlerde tekrarlanabilir */
    rowKey: string;
}

const toPositiveInt = (v: unknown): number | undefined =>
    typeof v === 'number' && v > 0
        ? v
        : typeof v === 'string' && /^\d+$/.test(v) && parseInt(v, 10) > 0
          ? parseInt(v, 10)
          : undefined;

const toAlternativeIds = (h: Record<string, unknown>): number[] => {
    const rawIds = Array.isArray(h.alternative_hotel_ids)
        ? h.alternative_hotel_ids
        : h.alternative_hotel_id != null
          ? [h.alternative_hotel_id]
          : [];
    const ids = rawIds.map(toPositiveInt).filter((x): x is number => typeof x === 'number');
    return Array.from(new Set(ids));
};

/**
 * tour_hotels meta'sından paket/gece bilgisiyle tüm otel satırlarını import sırasıyla döndürür.
 * Aynı hotel_id farklı paketlerde birden fazla satır olabilir.
 */
export function extractOrderedHotelEntries(tourHotelsMeta: unknown): TourHotelEntry[] {
    const entries: TourHotelEntry[] = [];

    if (!tourHotelsMeta) return entries;

    let items: unknown[] = [];
    if (Array.isArray(tourHotelsMeta)) {
        items = tourHotelsMeta;
    } else if (typeof tourHotelsMeta === 'string') {
        try {
            const parsed = JSON.parse(tourHotelsMeta);
            items = Array.isArray(parsed) ? parsed : [];
        } catch {
            return entries;
        }
    }

    let rowIndex = 0;
    const pushEntry = (raw: Record<string, unknown>) => {
        const hotelId = toPositiveInt(raw.hotel_id ?? raw.hotel);
        if (!hotelId) return;

        const meals = raw.meals;
        const nightsRaw = raw.nights;
        const packageRaw = raw.package;

        entries.push({
            hotelId,
            nights:
                typeof nightsRaw === 'number'
                    ? nightsRaw
                    : typeof nightsRaw === 'string' && /^\d+$/.test(nightsRaw)
                      ? parseInt(nightsRaw, 10)
                      : undefined,
            meals: Array.isArray(meals)
                ? meals.filter((m): m is string => typeof m === 'string')
                : typeof meals === 'string'
                  ? (() => {
                        try {
                            const a = JSON.parse(meals);
                            return Array.isArray(a) ? a.filter((m): m is string => typeof m === 'string') : [];
                        } catch {
                            return [];
                        }
                    })()
                  : [],
            mealsCustom: typeof raw.meals_custom === 'string' ? raw.meals_custom : '',
            package: typeof packageRaw === 'string' ? packageRaw.trim() : packageRaw != null ? String(packageRaw).trim() : '',
            alternativeHotelIds: toAlternativeIds(raw),
            rowKey: `hotel-entry-${rowIndex}-${hotelId}-${typeof packageRaw === 'string' ? packageRaw.trim() : String(packageRaw ?? '')}`,
        });
        rowIndex += 1;
    };

    items.forEach((item: unknown) => {
        if (!item || typeof item !== 'object') return;
        const record = item as Record<string, unknown>;

        const list = record.hotels ?? record.hotels_widget;
        if (Array.isArray(list) && list.length > 0) {
            list.forEach((h) => {
                if (h && typeof h === 'object') pushEntry(h as Record<string, unknown>);
            });
            return;
        }

        const singleId = toPositiveInt(record.hotel_id ?? record.hotel);
        if (singleId) {
            pushEntry(record);
        }
    });

    return entries;
}

export function sortHotelsByIdOrder<T extends { id: number }>(hotels: T[], orderedIds: number[]): T[] {
    if (orderedIds.length === 0) return hotels;
    const orderMap = new Map(orderedIds.map((id, index) => [id, index]));
    return [...hotels].sort((a, b) => (orderMap.get(a.id) ?? 9999) - (orderMap.get(b.id) ?? 9999));
}
